import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService, ConfigStateService, EnvironmentService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { OAuthService } from 'angular-oauth2-oidc';
import { from } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  standalone: true,
  selector: 'app-custom-login',
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class CustomLoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private oAuthService = inject(OAuthService);
  private authService = inject(AuthService);
  private configState = inject(ConfigStateService);
  private environmentService = inject(EnvironmentService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  loginForm!: FormGroup;
  showPassword = false;
  isLoading = false;

  private returnUrl = '/dashboard';

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.maxLength(255)]],
      password: ['', [Validators.required, Validators.maxLength(128)]],
      rememberMe: [false],
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    // Zaten giriş yapmışsa dashboard'a yönlendir
    if (this.authService.isAuthenticated) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const { email, password, rememberMe } = this.loginForm.value;

    // ABP'nin strategy'sini bypass edip OAuthService'i doğrudan kullanıyoruz
    from(this.oAuthService.fetchTokenUsingPasswordFlow(email, password))
      .pipe(
        switchMap(() => {
          // Token alındı, şimdi uygulama durumunu yenile (kullanıcı bilgileri, yetkiler vs.)
          if (rememberMe) {
            this.oAuthService.setupAutomaticSilentRefresh();
          }
          return this.configState.refreshAppState();
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => {
          this.router.navigateByUrl(this.returnUrl);
        },
        error: (err: any) => {
          const errorDesc = err?.error?.error_description || err?.error_description || '';
          const errorMsg = err?.error?.error?.message || err?.message || '';
          let message = 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';

          if (errorDesc.includes('invalid_username_or_password') || errorDesc.includes('Invalid username or password')) {
            message = 'E-posta veya şifre hatalı. Lütfen tekrar deneyin.';
          } else if (errorDesc.includes('locked_out') || errorDesc.includes('locked out')) {
            message = 'Hesabınız geçici olarak kilitlendi. Lütfen daha sonra tekrar deneyin.';
          } else if (errorDesc.includes('not_allowed') || errorDesc.includes('not allowed')) {
            message = 'Bu hesap ile giriş yapılamıyor. Lütfen yöneticiyle iletişime geçin.';
          } else if (errorDesc) {
            message = errorDesc;
          } else if (errorMsg) {
            message = errorMsg;
          }

          this.toasterService.error(message, 'Giriş Başarısız', { life: 5000 });
        },
      });
  }

  loginWithGoogle(): void {
    const apiUrl = environment.apis.default.url;
    const returnUrl = encodeURIComponent(window.location.origin + '/dashboard');
    window.location.href = `${apiUrl}/Account/Login?handler=ExternalLogin&provider=Google&returnUrl=${returnUrl}`;
  }
}
